/**
 * Playbooks slice (PX-W6.5) — the live playbooks table + the tenant-policy
 * panel. One thunk fetches both (the policy panel renders only when present;
 * a missing policy is an empty panel, not an error).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as service from './services/playbooksService.http'
import type { Playbook, TenantPolicy } from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

interface PlaybooksPayload {
  orgId: string | null
  playbooks: Playbook[]
  /**
   * Null when the org has no policy row yet (a 404 — the panel's empty
   * state). Fix-back review #3: every OTHER policy failure used to be
   * swallowed here too, rendering an EMPTY panel against a 403/500.
   */
  policy: TenantPolicy | null
  /**
   * A non-404 policy failure, carried on the FULFILLED payload so the
   * successfully-fetched playbooks table survives it (a rejection would
   * lose the rows) while the policy panel surfaces the error.
   */
  policyError: ApiError | null
}

export const fetchPlaybooks = createAsyncThunk<PlaybooksPayload, void, { rejectValue: ApiError }>(
  'playbooks/fetchPlaybooks',
  async (_, { getState, rejectWithValue }) => {
    const orgId = orgKey(getState)
    try {
      const playbooks = await service.playbooksService.list()
      let policy: TenantPolicy | null = null
      let policyError: ApiError | null = null
      try {
        policy = await service.playbooksService.policy()
      } catch (error) {
        // ONLY the 404 ("no policy row yet") is the empty panel. A 403/500
        // must surface — silently rendering an empty policy panel against a
        // refusal hides an access question behind furniture.
        const apiError = rejected(error)
        if (apiError.status !== 404) policyError = apiError
      }
      return { orgId, playbooks, policy, policyError }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { playbooks: PlaybooksState }).playbooks.cache, orgKey(getState))
  }
)

export interface PlaybooksState {
  playbooks: Playbook[]
  policy: TenantPolicy | null
  cache: FetchCache
  /** The playbooks TABLE's fetch error. */
  error: ApiError | null
  /** The POLICY panel's own error (403/500 surfaced; 404 is just `policy: null`). */
  policyError: ApiError | null
}

const initialState: PlaybooksState = {
  playbooks: [],
  policy: null,
  cache: initialFetchCache,
  error: null,
  policyError: null
}

export const playbooksSlice = createSlice({
  name: 'playbooks',
  initialState,
  reducers: {
    playbooksRetryClicked(state) {
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaybooks.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
        state.policyError = null
      })
      .addCase(fetchPlaybooks.fulfilled, (state, action: PayloadAction<PlaybooksPayload>) => {
        fetchSucceeded(state.cache, action.payload.orgId)
        state.playbooks = action.payload.playbooks
        state.policy = action.payload.policy
        state.policyError = action.payload.policyError
      })
      .addCase(fetchPlaybooks.rejected, (state, action) => {
        // Only the TABLE fetch rejects the thunk — the policy failure rides
        // the fulfilled payload above.
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
  }
})

export const { playbooksRetryClicked } = playbooksSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithPlaybooks {
  playbooks: PlaybooksState
}

export const selectPlaybooks = (s: WithPlaybooks): Playbook[] => s.playbooks.playbooks
export const selectTenantPolicy = (s: WithPlaybooks): TenantPolicy | null =>
  s.playbooks.policy
export const selectPlaybooksCache = (s: WithPlaybooks): FetchCache => s.playbooks.cache
export const selectPlaybooksError = (s: WithPlaybooks): ApiError | null => s.playbooks.error
export const selectPolicyError = (s: WithPlaybooks): ApiError | null =>
  s.playbooks.policyError
